import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Eye } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface TicketCardProps {
  ticket: {
    id: string;
    ticket_number: string;
    username: string;
    type: string;
    subject: string;
    description: string;
    status: string;
    created_at: string;
  };
  onStatusChange: (ticketId: string, newStatus: string) => void;
  onViewDetails: (ticket: any) => void;
  isLoading?: boolean;
}

const typeLabels: Record<string, string> = {
  request: 'Solicitação',
  suggestion: 'Sugestão',
  compliment: 'Elogio',
  complaint: 'Reclamação',
  report: 'Denúncia',
};

const typeColors: Record<string, string> = {
  request: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  suggestion: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  compliment: 'bg-green-500/20 text-green-400 border-green-500/30',
  complaint: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  report: 'bg-destructive/20 text-destructive border-destructive/30',
};

const statusLabels: Record<string, string> = {
  open: 'Aberto',
  in_progress: 'Em Andamento',
  resolved: 'Resolvido',
  closed: 'Fechado',
};

const TicketCard: React.FC<TicketCardProps> = ({
  ticket,
  onStatusChange,
  onViewDetails,
  isLoading = false,
}) => {
  return (
    <Card className="bg-black border-white/10 hover:border-primary/50 transition-colors">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-semibold text-white">#{ticket.ticket_number}</h3>
              <Badge variant="outline" className={typeColors[ticket.type] || ''}>
                {typeLabels[ticket.type] || ticket.type}
              </Badge>
            </div>
            <p className="text-sm text-white/70 mb-1">
              Usuário: <span className="text-white font-medium">{ticket.username}</span>
            </p>
            <p className="text-sm text-white/70">
              {format(new Date(ticket.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
            </p>
          </div>
        </div>

        <div className="mb-4">
          <h4 className="font-semibold text-white mb-1">{ticket.subject}</h4>
          <p className="text-sm text-white/70 line-clamp-2">{ticket.description}</p>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex-1">
            <Select
              value={ticket.status}
              onValueChange={(value) => onStatusChange(ticket.id, value)}
              disabled={isLoading}
            >
              <SelectTrigger className="bg-background/50 border-border">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Aberto</SelectItem>
                <SelectItem value="in_progress">Em Andamento</SelectItem>
                <SelectItem value="resolved">Resolvido</SelectItem>
                <SelectItem value="closed">Fechado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onViewDetails(ticket)}
            className="border-border"
          >
            <Eye className="h-4 w-4 mr-1" />
            Detalhes
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TicketCard;
